/**
 * Declarative catalog of every ATS/HRIS integration the settings page can
 * connect. One entry replaces the old per-provider render method + dedupe
 * check + sync handler that used to be copy-pasted through
 * SettingsFormAtsComponent.jsx.
 *
 * field.secret => rendered as a password input and masked in the connected
 * integrations list (the old UI printed raw API keys and passwords on screen).
 */
import { configMode } from 'src/_shared/api/';

export const WORKATO_PROVIDERS = [
	{ value: 'adpWorkato', title: 'ADP' },
	{ value: 'applicantProWorkato', title: 'ApplicantPro' },
	{ value: 'apploiWorkato', title: 'Apploi' },
	{ value: 'bullhornWorkato', title: 'Bullhorn' },
	{ value: 'crelateWorkato', title: 'Crelate' },
	{ value: 'fileWorkato', title: 'File' },
	{ value: 'fountainWorkato', title: 'Fountain' },
	{ value: 'greenhouseWorkato', title: 'Greenhouse' },
	{ value: 'hiringThingWorkato', title: 'HiringThing' },
	{ value: 'hubspotWorkato', title: 'HubSpot' },
	{ value: 'iCIMSWorkato', title: 'iCIMS' },
	{ value: 'jobViteWorkato', title: 'Jobvite' },
	{ value: 'leverWorkato', title: 'Lever' },
	{ value: 'neogovWorkato', title: 'NEOGOV' },
	{ value: 'paycorWorkato', title: 'Paycor' },
	{ value: 'paylocityWorkato', title: 'Paylocity' },
	{ value: 'smartRecruitersWorkato', title: 'SmartRecruiters' },
	{ value: 'talentLinkWorkato', title: 'TalentLink' },
	{ value: 'talentreefWorkato', title: 'Talent Tree' },
	{ value: 'workdayWorkato', title: 'Workday' },
].map((p) => ({ ...p, family: 'workato', fields: [] }));

const text = (name, label, extra = {}) => ({ name, label, ...extra });
const secret = (name, label) => text(name, label, { secret: true });

export const LEGACY_PROVIDERS = [
	{
		value: 'iCIMS',
		title: 'iCIMS',
		fields: [
			text('ATSCustomerId', 'Customer ID', { formName: 'atsIntegrationCustomerID' }),
			text('ATSPortalId', 'Portal ID', { formName: 'atsIntegrationPortalID' }),
			text('ATSAPIUserName', 'User Name', { formName: 'atsIntegrationUserName' }),
			secret('ATSAPIPassword', 'Password', { formName: 'atsIntegrationPassword' }),
		],
		// ATS Name + Customer ID + Portal ID must be unique together.
		dedupeKeys: ['ATSCustomerId', 'ATSPortalId'],
		syncEndpoint: { dev: 'icims-scheduler-api-trigger', prod: 'icims-scheduler-api-trigger-prod' },
		syncConfirm: true,
	},
	{
		value: 'Greenhouse',
		title: 'Greenhouse',
		fields: [
			secret('ATSAPIKey', 'API Key or URL'),
			secret('HarvestAPIkey', 'Harvest API Key'),
			text('sourceCode', 'Source Code'),
			text('sourceId', 'Source ID'),
			text('apiUserId', 'API User ID'),
		],
		syncEndpoint: {
			dev: 'greenhouse-scheduler-api-trigger-dev',
			prod: 'greenhouse-scheduler-api-trigger-prod',
		},
		supportsDepartmentImport: true,
	},
	{
		value: 'ClearCompany',
		title: 'ClearCompany',
		fields: [
			secret('ATSAPIKey', 'API Key or URL'),
			text('userName', 'User Name'),
			secret('password', 'Password'),
		],
		syncEndpoint: {
			dev: 'clearcompany-scheduler-api-trigger-dev',
			prod: 'clearcompany-scheduler-api-trigger-prod',
		},
	},
	{
		value: 'Lever',
		title: 'Lever',
		fields: [secret('ATSAPIKey', 'API Key or URL')],
		syncEndpoint: { dev: 'lever-scheduler-api-trigger-dev', prod: 'lever-scheduler-api-trigger-prod' },
	},
	{
		value: 'Ultipro',
		title: 'UKG Pro (Ultipro)',
		fields: [
			secret('ClientAccessKey', 'Client Access Key'),
			text('UserName', 'User Name'),
			secret('Password', 'Password'),
			secret('UserAccessKey', 'User Access Key'),
			text('SourceId', 'Source ID'),
			text('JobReportPath', 'Job Report Path'),
			text('CandidateReportPath', 'Candidate Report Path'),
		],
		syncEndpoint: {
			dev: 'ultipro-scheduler-api-trigger-dev',
			prod: 'ultipro-scheduler-api-trigger-prod',
		},
	},
	{
		value: 'Crelate',
		title: 'Crelate',
		fields: [secret('ATSAPIKey', 'API Key'), text('CustomerName', 'Customer Name')],
		syncEndpoint: {
			dev: 'crelate-scheduler-api-trigger-dev',
			prod: 'crelate-scheduler-api-trigger-prod',
		},
	},
	{
		value: 'SeaWorld',
		title: 'SeaWorld',
		fields: [
			text('ClientId', 'Client ID'),
			secret('ClientSecret', 'Client Secret'),
			text('ClientScope', 'Client Scope'),
		],
		syncEndpoint: { dev: 'seaworld-scheduler-api-trigger-dev', prod: 'seaworld-scheduler-api-trigger-prod' },
	},
	{
		value: 'ReferVets',
		title: 'ReferVets',
		fields: [text('UserName', 'User Name'), secret('Token', 'Token')],
	},
	{
		value: 'Silkroad',
		title: 'Silkroad',
		fields: [],
		syncEndpoint: { dev: null, prod: 'silkroad-scheduler-api-trigger-prod' },
	},
	{
		value: 'TalentReef',
		title: 'TalentReef',
		fields: [secret('ATSAPIKey', 'API Key or URL'), text('ATSPortalId', 'Portal Code')],
		dedupeKeys: ['ATSAPIKey', 'ATSPortalId'],
		syncEndpoint: { dev: null, prod: 'talentreef-scheduler-api-trigger-prod' },
	},
	{
		value: 'HiBob',
		title: 'HiBob',
		fields: [secret('ATSAPIToken', 'API Token')],
	},
	{
		value: 'Other',
		title: 'Other',
		fields: [secret('ATSAPIKey', 'API Key or URL')],
	},
].map((p) => ({ family: 'legacy', ...p }));

export const ALL_PROVIDERS = [...WORKATO_PROVIDERS, ...LEGACY_PROVIDERS];

export const providerByValue = (value) =>
	ALL_PROVIDERS.find((p) => p.value === value) || null;

/** Display title for a stored atsIntegration entry. */
export const providerTitle = (atsName) => {
	const provider = providerByValue(atsName);
	if (provider) return provider.title;
	return atsName || 'Unknown';
};

export const isWorkato = (atsName) => (atsName || '').includes('Workato');

export const syncEndpointFor = (atsName) => {
	const provider = providerByValue(atsName);
	const endpoints = provider?.syncEndpoint;
	if (!endpoints) return null;
	return (configMode === 'DEV' ? endpoints.dev : endpoints.prod) || null;
};

/** Values that must never be rendered on screen. */
export const secretFieldNames = new Set(
	LEGACY_PROVIDERS.flatMap((p) => p.fields.filter((f) => f.secret).map((f) => f.name)).concat([
		'ATSAPIKey',
		'ATSAPIPassword',
		'HarvestAPIkey',
		'password',
		'Password',
		'ClientSecret',
		'Token',
		'ATSAPIToken',
		'ClientAccessKey',
		'UserAccessKey',
	])
);

export const maskSecret = (value) => {
	const s = String(value ?? '');
	if (!s) return '';
	return s.length > 4 ? `••••${s.slice(-4)}` : '••••';
};
